import { useEventUserRole } from '@shared/components/AuthProvider';
import { useEventScopedCoupleRole } from '@shared/hooks/useEventScopedCoupleRole/useEventScopedCoupleRole';

type UseCashFundAccessArgs = Readonly<{
  /** Event whose cash fund is being spent — NOT the auth session's active event. */
  eventId: string | undefined;
}>;

type CashFundAccess = Readonly<{
  /** True when the logged-in user is owner/admin (either partner) of THIS event. */
  isCouple: boolean;
  /**
   * True for a super admin on an event they are not part of — support spending a couple's
   * money for them. Mutually exclusive with `isCouple`: staff on their own event are a couple.
   */
  isSuperAdminOnBehalf: boolean;
  /** Either of the above: this user may be offered the event's cash fund. */
  canUseCashFund: boolean;
  isLoading: boolean;
}>;

/**
 * Who may be offered an event's cash fund, and whether that is support acting for the couple.
 *
 * Couples are resolved from the event's real membership row (`useEventScopedCoupleRole`), which
 * deliberately ignores the account-level superAdmin flag. This hook adds the staff case back on
 * top instead of loosening that hook, so every other consumer keeps the unmasked role.
 *
 * Callers must keep staff out of experiment exposure: an on-behalf user is not part of the
 * couple cohort, so skip the flag read for them rather than bucketing them.
 */
export const useCashFundAccess = ({ eventId }: UseCashFundAccessArgs): CashFundAccess => {
  const { isSuperAdmin } = useEventUserRole();
  const { isCouple, isLoading } = useEventScopedCoupleRole({ eventId });

  // Only once the membership row has resolved — before that, a staff member who owns this event
  // would briefly look like an on-behalf booker.
  const isSuperAdminOnBehalf = isSuperAdmin && !isCouple && !isLoading;

  return {
    isCouple,
    isSuperAdminOnBehalf,
    canUseCashFund: isCouple || isSuperAdminOnBehalf,
    isLoading,
  };
};
